/**
 * Router shell. AuthProvider wraps everything so views can read user/token.
 */
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth.js";

import Login              from "./views/Login.js";
import Signup             from "./views/Signup.js";
import TrainerDashboard   from "./views/TrainerDashboard.js";
import CaseDetail         from "./views/CaseDetail.js";
import SessionDetail      from "./views/SessionDetail.js";
import AdminApprovals     from "./views/AdminApprovals.js";
import AdminUsers         from "./views/AdminUsers.js";
import CaseDashboard      from "./views/CaseDashboard.js";
import CaseSessionDetail  from "./views/CaseSessionDetail.js";
import JoinLanding        from "./views/JoinLanding.js";
import Studio                from "./views/Studio.js";
import Presets               from "./views/Presets.js";
import SoraDemoSubapp        from "./views/SoraDemoSubapp.js";
import VisioDemoSubapp       from "./views/VisioDemoSubapp.js";
import MyndScapeDemoSubapp   from "./views/MyndScapeDemoSubapp.js";
import SgimacogDemoSubapp    from "./views/SgimacogDemoSubapp.js";
import DesignDemo            from "./views/DesignDemo.js";

export function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login"  element={<PublicOnly><Login /></PublicOnly>} />
        <Route path="/signup" element={<PublicOnly><Signup /></PublicOnly>} />

        {/* Trainer */}
        <Route path="/dashboard"
               element={<RequireAuth roles={["trainer", "admin"]}><TrainerDashboard /></RequireAuth>} />
        <Route path="/dashboard/cases/:id"
               element={<RequireAuth roles={["trainer", "admin"]}><CaseDetail /></RequireAuth>} />
        <Route path="/dashboard/sessions/:id"
               element={<RequireAuth roles={["trainer", "admin"]}><SessionDetail /></RequireAuth>} />
        <Route path="/dashboard/presets"
               element={<RequireAuth roles={["trainer", "admin"]}><Presets /></RequireAuth>} />

        {/* Admin */}
        <Route path="/admin/approvals"
               element={<RequireAuth roles={["admin"]}><AdminApprovals /></RequireAuth>} />
        <Route path="/admin/users"
               element={<RequireAuth roles={["admin"]}><AdminUsers /></RequireAuth>} />

        {/* Case */}
        <Route path="/me"
               element={<RequireAuth roles={["case"]}><CaseDashboard /></RequireAuth>} />
        <Route path="/me/sessions/:id"
               element={<RequireAuth roles={["case"]}><CaseSessionDetail /></RequireAuth>} />

        {/* Studio (P3) */}
        <Route path="/studio/:sessionId"
               element={<RequireAuth roles={["trainer", "admin", "case"]}><Studio /></RequireAuth>} />

        {/* Public join landing — case enters the system here */}
        <Route path="/join/:token" element={<JoinLanding />} />

        {/* Sub-app mocks for telerehab integration (P4-P5).
            Defence in depth: each subapp also runs isDemoHostAllowed()
            and 404s on production hostname — these RequireAuth wrappers
            are the second gate so anonymous strangers can't reach the
            mock metric loop even on staging. Real cross-repo sub-apps
            (sgimacog-web, NFB-Webapp, rppg-webapp, myndscape-web) won't
            live under /subapp/* in CogniHome. */}
        <Route path="/subapp/sora-demo"
               element={<RequireAuth roles={["trainer", "case", "admin"]}><SoraDemoSubapp /></RequireAuth>} />
        <Route path="/subapp/visio-demo"
               element={<RequireAuth roles={["trainer", "case", "admin"]}><VisioDemoSubapp /></RequireAuth>} />
        <Route path="/subapp/myndscape-demo"
               element={<RequireAuth roles={["trainer", "case", "admin"]}><MyndScapeDemoSubapp /></RequireAuth>} />
        <Route path="/subapp/sgimacog-demo"
               element={<RequireAuth roles={["trainer", "case", "admin"]}><SgimacogDemoSubapp /></RequireAuth>} />

        {/* Design lock-in mockup — public for review */}
        <Route path="/demo" element={<DesignDemo />} />

        <Route path="/" element={<RootRedirect />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </AuthProvider>
  );
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user)   return <Navigate to="/login" replace />;
  if (user.role === "case")  return <Navigate to="/me" replace />;
  if (user.role === "admin") return <Navigate to="/admin/approvals" replace />;
  return <Navigate to="/dashboard" replace />;
}

function RequireAuth({ children, roles }: { children: JSX.Element; roles: string[] }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  if (!roles.includes(user.role)) return <ForbiddenScreen role={user.role} />;
  return children;
}

function PublicOnly({ children }: { children: JSX.Element }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (user) {
    const target = user.role === "case" ? "/me" : user.role === "admin" ? "/admin/approvals" : "/dashboard";
    return <Navigate to={target} replace />;
  }
  return children;
}

function Loading() {
  return <main className="page page--auth"><div className="serial">…</div></main>;
}

function ForbiddenScreen({ role }: { role: string }) {
  return (
    <main className="page page--auth">
      <div className="serial" style={{ marginBottom: "var(--s-3)" }}>403 · forbidden</div>
      <h1>權限不足</h1>
      <p className="muted" style={{ marginTop: "var(--s-3)" }}>你目前以 <code>{role}</code> 身分登入，無法看這頁。</p>
    </main>
  );
}

function NotFound() {
  return (
    <main className="page page--auth">
      <div className="serial" style={{ marginBottom: "var(--s-3)" }}>404 · not found</div>
      <h1>找不到這頁</h1>
    </main>
  );
}
